/*
 * Home: a greeting with the company's date, the workflow from request to paid (like Jobber's), today's visits beside a big
 * map of them, one Needs you list (loose ends and visit checks), what is left to set up (owners, hidden once done), the
 * companies below for HQ and brand owners, and this week's numbers at the bottom.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  addDays,
  requestStats,
  type LooseEnd,
  type MoneyToDo,
  type OverviewAnswer,
  type OverviewRow,
  type RequestStats,
  type TodoItem,
} from "@steward/shared";
import {
  useClientSummary,
  useHome,
  useKeys,
  useLooseEnds,
  usePeople,
  useReport,
  useSettings,
  useSnooze,
  useVisits,
} from "../api/hooks";
import { api } from "../api/client";
import { useMoneyToDo, useRequests } from "../api/money";
import { useSession } from "../app/session";
import { clock, money, plural, shortDate, todayIn } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { Banner, Button, Card, EmptyState, LoadError, Page, Skeleton, StatusLabel } from "../components/parts";
import { BarChart, Legend, type Series } from "../components/Charts";
import { CrewChips, VisitStatus } from "../components/work";
import { VisitMap } from "../components/VisitMap";
import { OverviewTiles } from "./HeadOffice";
import { usePhone } from "../components/usePhone";
import { moneyRows, PhoneHome } from "./HomePhone";
import { useOverview, useVaultGraph } from "../api/hq";
import { VaultBrain } from "../components/VaultBrain";

function greeting(tz: string): { hello: string; date: string } {
  const now = new Date();
  const hour = Number(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", hourCycle: "h23" }).format(now));
  const hello = hour < 5 ? "Good evening" : hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening"; // midnight to 5 AM still feels like night
  const date = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "long", month: "long", day: "numeric" }).format(now);
  return { hello, date };
}

interface Need {
  what: string;
  hint: string;
  icon: IconName;
  to: string;
  params?: { ref: string };
}

export function Home() {
  const { company } = useSession();
  return company.level === "hq" ? <HqHome /> : <PlaceHome />;
}

/**
 * Home at The Trades (HQ): the head office view. The home service work (schedule, needs you, the map, Stripe) lives in the
 * brands and locations; HQ has no Stripe of its own (owner answer 59).
 */
function HqHome() {
  const { me, company, role } = useSession();
  const q = useOverview(company.id);
  const brain = useVaultGraph(role === "owner");
  const { hello, date } = greeting(company.timezone);
  const first = me.name.split(" ")[0] ?? me.name;
  const grid = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(CHART_MAX);
  // The chart grows or shrinks so its card ends level with the company list beside it, leaving the rest of the screen to
  // the brain (owner, 2026-11-03: "make the dash for home not scrollable" on every computer).
  useEffect(() => {
    const box = grid.current;
    const chart = box?.children[0] as HTMLElement | undefined;
    const list = box?.children[1] as HTMLElement | undefined;
    if (!box || !chart || !list) return;
    const fit = () => {
      const off = list.offsetHeight - chart.offsetHeight;
      const side = getComputedStyle(box).gridTemplateColumns.split(" ").length > 1; // stacked on a phone
      setChartHeight((h) => (side ? Math.round(Math.min(CHART_MAX, Math.max(CHART_MIN, h + off))) : CHART_MAX));
    };
    const seen = new ResizeObserver(fit);
    seen.observe(list);
    return () => seen.disconnect();
  }, [q.data]);
  return (
    <Page title={`${hello}, ${first}`} subtitle={date}>
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : q.data ? (
        <div className="stack">
          <OverviewTiles totals={q.data.totals} />
          <div className="home-grid" ref={grid}>
            <CollectedByMonth
              months={q.data.months}
              groups={brandGroups(q.data.rows, company.id)}
              hq={{ id: company.id, name: company.name }}
              height={chartHeight}
            />
            <CompanyRanking groups={brandGroups(q.data.rows, company.id)} />
          </div>
          {brain.data ? (
            <section className="card hq-brain">
              <VaultBrain graph={brain.data} />
              <div className="hq-brain-head">
                <h2>Your brain</h2>
                <span className="muted small num">
                  {plural(brain.data.nodes.length, "note")} ·{" "}
                  {brain.data.nodes.filter((n) => n.edited && Date.now() - n.edited < 86_400_000).length} edited today
                </span>
              </div>
            </section>
          ) : null}
        </div>
      ) : (
        <Skeleton rows={6} />
      )}
    </Page>
  );
}

/** HQ Home chart height, tallest and shortest. */
const CHART_MAX = 240;
const CHART_MIN = 140;

const SHORT_MONTH = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });
const LONG_MONTH = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

const BRAND_TONES = ["c1", "c2", "c3", "c4", "c5"] as const;
interface BrandGroup {
  row: OverviewRow;
  /** The company and every one below it. */
  all: OverviewRow[];
  tone: Series["tone"];
  /** Its own brand color, when it has one (else the tone). */
  color?: string | undefined;
}

/**
 * A brand color made easy to see on the card: a near black brand (like The Marketers) is lifted to a grey, so its bar
 * does not vanish in dark mode.
 */
function chartColor(hex: string | null): string | undefined {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return undefined;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const light = (0.2126 * r! + 0.7152 * g! + 0.0722 * b!) / 255;
  if (light >= 0.18) return hex;
  const mix = (c: number) => Math.round(c + (200 - c) * 0.6);
  return `rgb(${String(mix(r!))},${String(mix(g!))},${String(mix(b!))})`;
}

/** Each company right under HQ with its locations, in its own brand color (a set color by name when it has none). */
function brandGroups(rows: OverviewRow[], hqId: string): BrandGroup[] {
  const under = (id: string): OverviewRow[] => rows.filter((r) => r.parentId === id).flatMap((r) => [r, ...under(r.companyId)]);
  return rows
    .filter((r) => r.parentId === hqId)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((r, i) => ({
      row: r,
      all: [r, ...under(r.companyId)],
      tone: BRAND_TONES[i % BRAND_TONES.length]!,
      color: chartColor(r.brandColor),
    }));
}

/**
 * Twelve months of money in, one bar per brand side by side, each in its brand color, with the month's total in small
 * type above. HQ's own money, if any, gets its own. Hovering a month lights its bars and lists each brand's money and
 * the total. This month is still going, so its tip says so.
 */
function CollectedByMonth({
  months,
  groups,
  hq,
  height,
}: {
  months: OverviewAnswer["months"];
  groups: BrandGroup[];
  hq: { id: string; name: string };
  height: number;
}) {
  const at = (m: string) => new Date(`${m}-15T12:00:00Z`);
  const last = months.length - 1;
  const parts: Series[] = [
    ...groups.map((g) => ({
      name: g.row.name,
      tone: g.tone,
      color: g.color,
      values: months.map((m) => g.all.reduce((a, x) => a + (m.byCompany[x.companyId] ?? 0), 0) / 100),
    })),
    { name: hq.name, tone: "soft" as const, values: months.map((m) => (m.byCompany[hq.id] ?? 0) / 100) },
  ].filter((s) => s.values.some((v) => v > 0));
  const series: Series[] = parts.length
    ? parts
    : [{ name: "Collected", values: months.map((m) => m.collectedCents / 100), tone: "main" }];
  return (
    <Card title="Collected by month">
      <BarChart
        labels={months.map((m) => SHORT_MONTH.format(at(m.month)))}
        tipLabels={months.map((m, i) => `${LONG_MONTH.format(at(m.month))}${i === last ? ", so far" : ""}`)}
        series={series}
        total
        format={(v) => money(Math.round(v * 100))}
        axisPrefix="$"
        height={height}
      />
      {parts.length > 1 ? (
        <Legend
          items={parts.map((s) => ({
            name: s.name,
            tone: s.tone,
            color: s.color,
            value: money(Math.round(s.values.reduce((a, v) => a + v, 0) * 100)),
          }))}
        />
      ) : null}
    </Card>
  );
}

/**
 * The brands and locations right under HQ, most money first, each with its locations added in. The bar is its share of
 * the last 30 days, in the brand's chart color. Tapping one switches into it.
 */
function CompanyRanking({ groups: brands }: { groups: BrandGroup[] }) {
  const { switchCompany } = useSession();
  const navigate = useNavigate();
  const groups = brands
    .map(({ row: r, all, tone, color }) => {
      const add = (k: "collected30Cents" | "customersServed" | "repeatingMonthlyCents") => all.reduce((a, x) => a + x[k], 0);
      return {
        row: r,
        tone,
        color,
        locations: all.filter((x) => x.level === "location").length,
        paused: all.some((x) => x.status === "paused"),
        billFailed: all.some((x) => x.billStatus === "failed"),
        collected: add("collected30Cents"),
        customers: add("customersServed"),
        repeating: add("repeatingMonthlyCents"),
      };
    })
    .sort((a, b) => b.collected - a.collected || a.row.name.localeCompare(b.row.name));
  const total = groups.reduce((a, g) => a + g.collected, 0);
  return (
    <Card title="The Trades" action={<span className="muted small">Last 30 days</span>} flush>
      {groups.length ? (
        <ul className="hq-cos">
          {groups.map((g) => (
            <li key={g.row.companyId}>
              <button
                type="button"
                className="hq-co"
                onClick={() => {
                  switchCompany(g.row.companyId);
                  void navigate({ to: "/home" });
                }}
              >
                <span className="hq-co-top">
                  <b>
                    <span
                      className={`chart-dot ${g.tone}`}
                      style={g.color ? { background: g.color } : undefined}
                      aria-hidden="true"
                    />
                    {g.row.name}
                  </b>
                  <span className="num">{money(g.collected)}</span>
                </span>
                <span className="hq-co-bar" aria-hidden="true">
                  <span
                    className={g.tone}
                    style={{
                      width: `${String(total ? Math.round((g.collected / total) * 100) : 0)}%`,
                      ...(g.color ? { background: g.color } : {}),
                    }}
                  />
                </span>
                <span className="hq-co-sub">
                  <span className="muted small num">
                    {g.customers || g.repeating
                      ? `${plural(g.customers, "customer")} · ${money(g.repeating)} a month repeating`
                      : "No customers yet"}
                    {g.locations && g.row.level === "brand" ? ` · ${plural(g.locations, "location")}` : ""}
                  </span>
                  {g.billFailed ? <StatusLabel status="critical">Fee card turned down</StatusLabel> : null}
                  {g.paused ? <StatusLabel status="inactive">Paused</StatusLabel> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="building" title="No brands yet">
          Add a brand or a location in Settings, Business info.
        </EmptyState>
      )}
    </Card>
  );
}

function PlaceHome() {
  const { me, company, companies, can } = useSession();
  const home = useHome(company.id, can("schedule.read"));
  const settings = useSettings(company.id);
  const people = usePeople(company.id);
  const keys = useKeys(company.id, can("keys.read"));
  const report = useReport(company.id, can("report.children"));
  const seesWork = can("schedule.read");
  const today = todayIn(company.timezone);
  // One look at the week either side of today: the day's list, late visits and visits with nobody on them.
  const visits = useVisits(company.id, { from: addDays(today, -7), to: addDays(today, 7) }, seesWork);
  const clientSummary = useClientSummary(company.id, can("client.read"));
  // Plan offers to send (owner, 2026-11-02): asked for a regular cleaning, or just cleaned and not on a plan.
  const planOffers = useQuery({
    queryKey: ["todo", company.id, "plan_offers"],
    queryFn: () => api<{ items: TodoItem[] }>("GET", "home/todo/plan_offers"),
    enabled: company.trade === "cleaning" && can("quote.update"),
  });
  const offersToSend = planOffers.data?.items.length ?? 0;
  const moneyToDo = useMoneyToDo(company.id, can("invoice.read"));
  const requests = useRequests(company.id, can("lead.read"));
  const looseEnds = useLooseEnds(can("invoice.read"));
  const snooze = useSnooze();
  const { hello, date } = greeting(company.timezone);
  const first = me.name.split(" ")[0] ?? me.name;
  const isBrand = companies.some((c) => c.parentId === company.id);

  const setup = [
    {
      done: me.twoStepOn,
      what: "Two-step sign in",
      hint: me.twoStepOn ? "On for your login" : "Keeps the business safe if a password leaks",
      to: "/settings/security",
    },
    ...(isBrand
      ? [
          {
            done: Boolean(company.brandColor),
            what: "Brand color",
            hint: "Every location of the brand uses it",
            to: "/settings/business",
          },
        ]
      : []),
    {
      done: Boolean(company.phone && company.email),
      what: "Business phone and email",
      hint: "Shown to clients on invoices and quotes",
      to: "/settings/business",
    },
    {
      done: (people.data?.length ?? 0) > 1,
      what: "Your team",
      hint: `${String(people.data?.length ?? 0)} on the team so far`,
      to: "/hr/team",
    },
    ...(can("keys.read")
      ? [
          {
            done: Boolean(keys.data?.some((k) => k.provider === "stripe")),
            what: "Stripe for card payments",
            hint: "Test key only in this build",
            to: "/settings/keys",
          },
        ]
      : []),
  ];
  const all = visits.data?.visits ?? [];
  const todays = all.filter((v) => v.date === today && v.status !== "skipped" && v.status !== "cancelled");
  const late = all.filter((v) => v.date < today && (v.status === "scheduled" || v.status === "in_progress"));
  const noCrew = all.filter((v) => v.date >= today && v.status === "scheduled" && !v.crew.length);
  const idle = clientSummary.data?.withoutJobs ?? 0;
  const one = (list: typeof all, what: string, many: string, hint: string, icon: IconName, to: string): Need[] => {
    const v = list[0];
    if (!v) return [];
    return list.length === 1
      ? [{ what, hint: `${v.clientName} on ${shortDate(v.date)}`, icon, to: "/visits/$ref", params: { ref: v.ref } }]
      : [{ what: many, hint, icon, to }];
  };
  // Needs you is one list: the loose ends (snooze to tomorrow) and the visit and client checks. Invoices to check, past due
  // and unanswered quotes stay out of it, since the workflow on top shows them with their totals.
  const ends = (looseEnds.data?.looseEnds ?? []).filter((e) => !ON_TOP.has(e.key));
  const needs: Need[] = [
    ...one(
      late,
      "1 visit not marked done",
      `${plural(late.length, "visit")} not marked done`,
      "From the last 7 days. Mark them done or skip them.",
      "warning",
      "/todo/late_visits",
    ),
    // The loose ends count visits with no crew too; this one is for people who cannot see loose ends.
    ...(looseEnds.data
      ? []
      : one(
          noCrew,
          "1 visit has no crew",
          `${plural(noCrew.length, "visit")} with no crew`,
          `In the next 7 days. The first is ${shortDate(noCrew[0]?.date)}.`,
          "team",
          "/todo/no_crew",
        )),
    ...(offersToSend
      ? [
          {
            what: `${plural(offersToSend, "plan offer")} to send`,
            hint: "They asked for a regular cleaning, or we just cleaned. Send each one their prices.",
            icon: "quotes" as const,
            to: "/todo/plan_offers",
          },
        ]
      : []),
    ...(idle
      ? [
          {
            what: `${plural(idle, "client")} with nothing booked`,
            hint: "Active clients with no job. Book them or set them not active.",
            icon: "clients" as const,
            to: "/todo/idle_clients",
          },
        ]
      : []),
  ];
  const needCount = ends.length + needs.length;
  const left = setup.filter((s) => !s.done).length;
  const kids = (report.data ?? []).filter((r) => r.companyId !== company.id);
  const h = home.data;
  const change =
    h && h.lastWeekValueCents > 0 ? Math.round(((h.weekValueCents - h.lastWeekValueCents) / h.lastWeekValueCents) * 100) : null;
  // The Today row under the mouse, so its pin on the map grows.
  const [hovered, setHovered] = useState<string | null>(null);
  // Set up hides once every step is done, so a finished list does not take room every day.
  const showSetup = can("company.update") && left > 0;
  const side = showSetup || kids.length > 0;
  const phone = usePhone();

  if (phone)
    return (
      <PhoneHome
        title={`${hello}, ${first}`}
        date={date}
        today={today}
        banners={
          settings.data && !settings.data.settings.messaging.live ? (
            <Banner type="notice">
              <b>Texts and emails are in test mode.</b> Nothing goes to clients or crew yet.
            </Banner>
          ) : null
        }
        seesWork={seesWork}
        todays={todays}
        loading={seesWork && !visits.data}
        todo={[
          ...ends.map((e) => ({
            key: e.key,
            icon: "clock" as const,
            what: `${e.title} (${String(e.count)})`,
            hint: e.detail,
            to: e.to,
          })),
          ...needs.map((n) => ({
            key: n.what,
            icon: n.icon,
            what: n.what,
            hint: n.hint,
            to: n.to,
            ...(n.params ? { params: n.params } : {}),
          })),
          ...moneyRows(moneyToDo.data, requests.data ? requestStats(requests.data.requests) : null),
          ...(showSetup
            ? [
                {
                  key: "setup",
                  icon: "settings" as const,
                  what: `${plural(left, "step")} left to set up`,
                  hint: setup.find((x) => !x.done)?.what ?? "",
                  to: setup.find((x) => !x.done)?.to ?? "/settings/business",
                },
              ]
            : []),
        ]}
        health={h}
      />
    );

  return (
    <Page title={`${hello}, ${first}`} subtitle={date}>
      {company.namesPrivate ? (
        <Banner type="notice">
          <b>This franchise keeps its customer names private.</b> You see its business info, people, settings and security. Its
          totals are on the Head office page.
        </Banner>
      ) : null}
      {settings.data && !settings.data.settings.messaging.live ? (
        <Banner
          type="notice"
          action={
            can("outbox.read") ? (
              <Link to="/settings/messages" className="btn secondary small">
                See messages
              </Link>
            ) : null
          }
        >
          <b>Texts and emails are in test mode.</b> They are saved but nothing goes to clients or crew yet.
        </Banner>
      ) : null}
      <Workflow
        money={moneyToDo.data}
        requests={requests.data ? requestStats(requests.data.requests) : null}
        activeJobs={h?.activeJobs ?? null}
        late={seesWork && visits.data ? late.length : null}
      />
      {seesWork ? (
        <Card
          title="Today"
          action={
            <span className="row">
              {todays.length ? (
                <span className="muted num">
                  {plural(todays.length, "visit")} · {money(todays.reduce((s, v) => s + v.totalCents, 0))}
                </span>
              ) : null}
              <Link to="/schedule" className="btn secondary small">
                Schedule
              </Link>
            </span>
          }
          flush
        >
          {visits.error ? (
            <LoadError error={visits.error} retry={() => void visits.refetch()} />
          ) : !visits.data ? (
            <Skeleton rows={3} />
          ) : todays.length ? (
            <div className="today-split">
              <div className="todo today-list">
                {todays.map((v) => (
                  <Link
                    key={v.ref}
                    to="/visits/$ref"
                    params={{ ref: v.ref }}
                    className="item visit-row"
                    onMouseEnter={() => setHovered(v.ref)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(v.ref)}
                    onBlur={() => setHovered(null)}
                  >
                    <span className="when num">{clock(v.startTime)}</span>
                    <div className="what">
                      <b>{v.clientName}</b>
                      <span>
                        {v.jobName}
                        {v.address ? ` · ${v.address}` : ""}
                      </span>
                      <CrewChips crew={v.crew} />
                    </div>
                    {v.status === "done" ? (
                      <VisitStatus status="done" />
                    ) : (
                      <span className="num value">{money(v.totalCents)}</span>
                    )}
                  </Link>
                ))}
              </div>
              <div className="today-map">
                <VisitMap visits={todays} active={hovered} live />
              </div>
            </div>
          ) : (
            <EmptyState icon="schedule" title="No visits today">
              Visits for the day show here with their time, client, address and crew, next to a map of the stops.
            </EmptyState>
          )}
        </Card>
      ) : null}
      <div className={`home-grid${side ? "" : " one"}`}>
        <Card title="Needs you" action={needCount ? <span className="muted num">{plural(needCount, "thing")}</span> : null} flush>
          {needCount ? (
            <div className="todo">
              {ends.map((e) => (
                <div key={e.key} className="item">
                  <Icon name={END_ICON[e.kind] ?? "warning"} />
                  <Link to={e.to} className="what" style={{ color: "inherit", textDecoration: "none" }}>
                    <b>
                      {e.title} <span className="num muted">{e.count}</span>
                    </b>
                    <span>{e.detail}</span>
                  </Link>
                  <Button type="tertiary" size="small" onClick={() => snooze.mutate({ key: e.key, until: addDays(today, 1) })}>
                    Tomorrow
                  </Button>
                </div>
              ))}
              {needs.map((n) => (
                <Link key={n.what} to={n.to} {...(n.params ? { params: n.params } : {})} className="item">
                  <Icon name={n.icon} />
                  <div className="what">
                    <b>{n.what}</b>
                    <span>{n.hint}</span>
                  </div>
                  <Icon name="chevronRight" size={18} />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState icon="check" title="Nothing needs you">
              Late visits, visits with no crew, clients with nothing booked and other loose ends show here.
            </EmptyState>
          )}
        </Card>
        {side ? (
          <div className="stack">
            {showSetup ? (
              <Card title="Getting set up" action={<span className="muted num">{`${String(left)} left`}</span>} flush>
                <div className="todo">
                  {setup.map((s) => (
                    <Link key={s.what} to={s.to} className="item" style={{ textDecoration: "none", color: "inherit" }}>
                      <span className={`check${s.done ? " done" : ""}`}>
                        <Icon name="check" size={14} />
                      </span>
                      <div className="what">
                        <b>{s.what}</b>
                        <span>{s.hint}</span>
                      </div>
                      <Icon name="chevronRight" size={18} />
                    </Link>
                  ))}
                </div>
              </Card>
            ) : null}
            {kids.length ? (
              <Card title="Your companies" action={<Link to="/insights">See all</Link>} flush>
                <div className="todo">
                  {kids.slice(0, 6).map((k) => (
                    <div className="item" key={k.companyId}>
                      <Icon name="building" />
                      <div className="what">
                        <b>{k.name}</b>
                        <span className="num">
                          {k.clients} {k.clients === 1 ? "client" : "clients"} · {k.activeJobs} active{" "}
                          {k.activeJobs === 1 ? "job" : "jobs"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}
          </div>
        ) : null}
      </div>
      {h ? (
        <section className="stack">
          <h2 className="section-title">This week</h2>
          <div className="tiles">
            <div className="tile">
              <span className="label">Job value</span>
              <span className="value">{money(h.weekValueCents)}</span>
              <span className={`hint${change === null ? "" : change >= 0 ? " up" : " down"}`}>
                {change === null
                  ? `Last week ${money(h.lastWeekValueCents)}`
                  : `${change >= 0 ? "Up" : "Down"} ${String(Math.abs(change))}% from last week (${money(h.lastWeekValueCents)})`}
              </span>
            </div>
            <div className="tile">
              <span className="label">Visits</span>
              <span className="value">{h.weekVisits}</span>
              <span className="hint">Monday to Sunday</span>
            </div>
            <div className="tile">
              <span className="label">Visits scheduled</span>
              <span className="value">{h.nextSevenDaysVisits}</span>
              <span className="hint">Next 7 days</span>
            </div>
            <div className="tile">
              <span className="label">Active clients</span>
              <span className="value">{h.activeClients}</span>
              <span className="hint">
                {h.activeJobs} active {h.activeJobs === 1 ? "job" : "jobs"}
              </span>
            </div>
          </div>
        </section>
      ) : null}
    </Page>
  );
}

/** Loose ends the workflow on top already shows with their totals. */
const ON_TOP = new Set(["to_check", "past_due", "unsigned_quotes"]);

const END_ICON: Partial<Record<LooseEnd["kind"], IconName>> = {
  still_clocked_in: "clock",
  no_crew: "team",
  failed_charges: "payments",
  expiring_files: "file",
  complaints: "message",
  new_applicants: "person",
};

interface FlowCol {
  key: "request" | "quote" | "job" | "invoice";
  name: string;
  icon: IconName;
  to: "/requests" | "/quotes" | "/jobs" | "/invoices";
  count: number;
  cents?: number;
  status: string;
  lines: { label: string; value: string }[];
}

/**
 * The work from first ask to paid, left to right like Jobber's Home: Requests, Quotes, Jobs, Invoices. Each column is one
 * link to its list, with the number that needs doing big and two smaller lines under it. A column the person may not see
 * is left out.
 */
function Workflow({
  money: m,
  requests,
  activeJobs,
  late,
}: {
  money: MoneyToDo | undefined;
  requests: RequestStats | null;
  activeJobs: number | null;
  late: number | null;
}) {
  const cols: FlowCol[] = [];
  if (requests)
    cols.push({
      key: "request",
      name: "Requests",
      icon: "requests",
      to: "/requests",
      count: requests.counts.new,
      status: "New",
      lines: [
        { label: "Quote sent", value: String(requests.counts.sent) },
        { label: "Last 30 days", value: String(requests.recent) },
      ],
    });
  if (m)
    cols.push({
      key: "quote",
      name: "Quotes",
      icon: "quotes",
      to: "/quotes",
      count: m.quotesApproved,
      cents: m.quotesApprovedCents,
      status: "Approved",
      lines: [
        { label: "Draft", value: String(m.quotesDraft) },
        { label: `Waiting on client (${String(m.quotesWaiting)})`, value: money(m.quotesWaitingCents) },
      ],
    });
  if (m || activeJobs !== null)
    cols.push({
      key: "job",
      name: "Jobs",
      icon: "jobs",
      // Done work waits as an invoice to check, so Ready to bill opens the invoices.
      to: m ? "/invoices" : "/jobs",
      count: m ? m.toCheck : (activeJobs ?? 0),
      ...(m ? { cents: m.toCheckCents } : {}),
      status: m ? "Ready to bill" : "Active",
      lines: [
        ...(m && activeJobs !== null ? [{ label: "Active", value: String(activeJobs) }] : []),
        ...(late !== null ? [{ label: "Not marked done", value: String(late) }] : []),
      ],
    });
  if (m)
    cols.push({
      key: "invoice",
      name: "Invoices",
      icon: "invoices",
      to: "/invoices",
      count: m.awaiting + m.pastDue,
      cents: m.awaitingCents,
      status: "Awaiting payment",
      lines: [
        { label: `Past due (${String(m.pastDue)})`, value: money(m.pastDueCents) },
        { label: "Cash or check", value: String(m.toCollect) },
      ],
    });
  if (!cols.length) return null;
  return (
    <nav className="flow" aria-label="Workflow">
      {cols.map((c) => (
        <Link key={c.key} to={c.to} className={`flow-col flow-${c.key}`}>
          <span className="flow-name">
            <Icon name={c.icon} size={16} />
            {c.name}
          </span>
          <span className="flow-big">
            <b className="num">{c.count}</b>
            {c.cents !== undefined ? <span className="num">{money(c.cents)}</span> : null}
          </span>
          <span className="flow-status">{c.status}</span>
          <span className="flow-lines">
            {c.lines.map((l) => (
              <span key={l.label}>
                <span>{l.label}</span>
                <span className="num">{l.value}</span>
              </span>
            ))}
          </span>
        </Link>
      ))}
    </nav>
  );
}
